import { just, nothing, type Maybe } from "./maybe";
import type { MapDocument, MapPage } from "./generated/zwMap";

export const MAP_PAGE_DEFAULT: MapPage = {
  width: 2400,
  height: 1600,
  frame: "none",
};
export const MAP_PAGE_PRESETS = [
  { value: "2400x1600", label: "Landscape · 2400 × 1600" },
  { value: "1600x2400", label: "Portrait · 1600 × 2400" },
  { value: "2000x2000", label: "Square · 2000 × 2000" },
  { value: "4000x3000", label: "Large · 4000 × 3000" },
  { value: "custom", label: "Custom" },
];
export const MAP_FRAME_OPTIONS = [
  { value: "none", label: "None" },
  { value: "pencil", label: "Pencil line" },
  { value: "double", label: "Double pencil" },
  { value: "ornate", label: "Cartographer’s corners" },
];
export function clampMapDimension(value: number): number {
  return Number.isFinite(value)
    ? Math.round(Math.max(64, Math.min(8192, value)))
    : 2400;
}
export function mapPage(doc: Pick<MapDocument, "page">): MapPage {
  return doc.page ?? { width: 4000, height: 4000, frame: "none" };
}
export function pointOnMap(
  doc: Pick<MapDocument, "page">,
  point: { x: number; y: number },
): boolean {
  const page = mapPage(doc);
  return (
    Math.abs(point.x - (page.centerX ?? 0)) <= page.width / 2 &&
    Math.abs(point.y - (page.centerY ?? 0)) <= page.height / 2
  );
}

/** Stable, slightly imperfect ink lines; shared by the editor and PNG export. */
export function drawMapFrame(
  ctx: CanvasRenderingContext2D,
  page: MapPage,
  toScreen: (point: { x: number; y: number }) => { x: number; y: number },
  zoom: number,
): void {
  if (page.frame === "none") return;
  const screenPoint = (point: {
    x: number;
    y: number;
  }): { x: number; y: number } =>
    toScreen({
      x: point.x + (page.centerX ?? 0),
      y: point.y + (page.centerY ?? 0),
    });
  const inset = Math.min(page.width, page.height) * 0.025;
  const gap = Math.max(4, inset * 0.22);
  const line = (
    a: { x: number; y: number },
    b: { x: number; y: number },
    seed: number,
  ): void => {
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const steps = Math.max(2, Math.ceil(length / 12));
    ctx.beginPath();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const wobble =
        i === 0 || i === steps ? 0 : Math.sin(i * 1.73 + seed) * 0.35;
      const p = screenPoint({
        x: a.x + (b.x - a.x) * t + wobble,
        y: a.y + (b.y - a.y) * t + wobble,
      });
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  };
  const rectangle = (margin: number): void => {
    const x = page.width / 2 - margin,
      y = page.height / 2 - margin;
    line({ x: -x, y: -y }, { x, y: -y }, 1);
    line({ x, y: -y }, { x, y }, 2);
    line({ x, y }, { x: -x, y }, 3);
    line({ x: -x, y }, { x: -x, y: -y }, 4);
  };
  ctx.save();
  ctx.strokeStyle = "#51483c";
  ctx.globalAlpha = 0.65;
  ctx.lineWidth = 1.3 * zoom;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  rectangle(inset);
  if (page.frame !== "pencil") {
    ctx.lineWidth = 0.8 * zoom;
    ctx.globalAlpha = 0.45;
    rectangle(inset + gap);
  }
  if (page.frame === "ornate") {
    ctx.globalAlpha = 0.65;
    const length = Math.min(page.width, page.height) * 0.055;
    for (const sx of [-1, 1])
      for (const sy of [-1, 1]) {
        const x = sx * (page.width / 2 - inset - gap * 2);
        const y = sy * (page.height / 2 - inset - gap * 2);
        line({ x: x - sx * length, y }, { x, y }, 5);
        line({ x, y }, { x, y: y - sy * length }, 6);
        const center = { x: x - sx * gap, y: y - sy * gap };
        const diamond = [
          { x: center.x - gap / 2, y: center.y },
          { x: center.x, y: center.y - gap / 2 },
          { x: center.x + gap / 2, y: center.y },
          { x: center.x, y: center.y + gap / 2 },
        ];
        ctx.beginPath();
        diamond.forEach((p, i) => {
          const s = screenPoint(p);
          if (i === 0) ctx.moveTo(s.x, s.y);
          else ctx.lineTo(s.x, s.y);
        });
        ctx.closePath();
        ctx.stroke();
      }
  }
  ctx.restore();
}

export function croppedMapPage(
  page: MapPage,
  start: { x: number; y: number },
  end: { x: number; y: number },
): Maybe<MapPage> {
  const cx = page.centerX ?? 0,
    cy = page.centerY ?? 0;
  const left = Math.max(cx - page.width / 2, Math.min(start.x, end.x));
  const top = Math.max(cy - page.height / 2, Math.min(start.y, end.y));
  const right = Math.min(cx + page.width / 2, Math.max(start.x, end.x));
  const bottom = Math.min(cy + page.height / 2, Math.max(start.y, end.y));
  const width = Math.floor(right - left),
    height = Math.floor(bottom - top);
  if (width < 64 || height < 64) return nothing;
  return just({
    ...page,
    width,
    height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  });
}

export function clampPointToMapPage(
  page: MapPage,
  point: { x: number; y: number },
): { x: number; y: number } {
  const cx = page.centerX ?? 0,
    cy = page.centerY ?? 0;
  return {
    x: Math.max(cx - page.width / 2, Math.min(cx + page.width / 2, point.x)),
    y: Math.max(cy - page.height / 2, Math.min(cy + page.height / 2, point.y)),
  };
}
